import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import * as api from "../../../api";
import useStyles from "../styles";

const useLocalStyles = makeStyles((theme) => ({
  roundsContainer: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? '#2e2e2e' : '#f9f9f9',
    borderRadius: "8px",
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  roundsTable: {
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    borderRadius: "8px",
    overflow: "hidden",
    transition: "background-color 0.3s ease",
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: "bold",
  },
  lowScoreRow: {
    backgroundColor: theme.palette.type === 'dark' ? '#2d4a2d' : '#e8f5e9',
  },
  highScoreRow: {
    backgroundColor: theme.palette.type === 'dark' ? '#4a2d3a' : '#fce4ec',
  },
  scoreCell: {
    fontWeight: "bold",
    fontSize: "1.1rem",
    "& .MuiChip-label": {
      color: theme.palette.type === 'dark' ? '#fff' : 'inherit',
    },
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  noRoundsMessage: {
    padding: theme.spacing(3),
    textAlign: "center",
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#f9f9f9',
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    transition: "background-color 0.3s ease",
  },
  logScoreForm: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    transition: "background-color 0.3s ease",
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
  },
  loginPrompt: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    borderRadius: "8px",
  },
}));

const CourseRoundsSection = ({ course, user, onScoreLogged }) => {
  const classes = useLocalStyles();
  const globalClasses = useStyles();
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedTeeIndex, setSelectedTeeIndex] = useState(0);
  const [useCustomRatings, setUseCustomRatings] = useState(false);
  const [customSlope, setCustomSlope] = useState("");
  const [customHandicap, setCustomHandicap] = useState("");
  const isMounted = useRef(true);
  const [formData, setFormData] = useState({
    score: "",
    holes: "",
    notes: "",
    date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    isMounted.current = true;
    fetchCourseRounds();
    return () => {
      isMounted.current = false;
    };
  }, [course._id]);

  // Update form holes when course loads/changes
  useEffect(() => {
    if (course?.holes) {
      setFormData(prev => ({
        ...prev,
        holes: String(course.holes)
      }));
    }
  }, [course?.holes]);

  const fetchCourseRounds = async () => {
    if (!course || !course._id) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.getCourseRounds(course._id);
      if (!isMounted.current) return;
      // Handle both direct array and {data: array} responses
      const roundsData = Array.isArray(response) ? response : (response.data || []);
      setRounds(roundsData);
    } catch (error) {
      if (!isMounted.current) return;
      console.error("Error fetching course rounds:", error);
      setRounds([]);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (document.activeElement) document.activeElement.blur();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTeeIndex(0);
    setUseCustomRatings(false);
    setCustomSlope("");
    setCustomHandicap("");
    const defaultHoles = course?.holes ? String(course.holes) : "";
    setFormData({
      score: "",
      holes: defaultHoles,
      notes: "",
      date: new Date().toISOString().split("T")[0],
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.score) {
      alert("Please enter a score");
      return;
    }

    if (isNaN(formData.score) || formData.score < 0) {
      alert("Score must be a positive number");
      return;
    }

    try {
      setFormLoading(true);
      
      let teeBoxSlope;
      let teeBoxHandicap;
      
      if (useCustomRatings) {
        // Use custom ratings entered by user
        teeBoxSlope = customSlope ? parseFloat(customSlope) : null;
        teeBoxHandicap = customHandicap ? parseFloat(customHandicap) : null;
      } else {
        // Get the selected tee box
        const selectedTee = course.teeBoxes && course.teeBoxes[selectedTeeIndex] 
          ? course.teeBoxes[selectedTeeIndex] 
          : null;
        
        // Extract slope (slopeRating) and handicap (courseRating) from tee box
        teeBoxSlope = selectedTee?.slope || null;
        teeBoxHandicap = selectedTee?.handicap || null;
      }
      
      await api.logRound(user.result._id, {
        courseId: course._id,
        courseName: course.name,
        score: parseInt(formData.score),
        holes: parseInt(formData.holes),
        notes: formData.notes,
        date: formData.date,
        courseRating: teeBoxHandicap,
        slopeRating: teeBoxSlope,
      });

      handleCloseDialog();
      
      // Refresh rounds and trigger callback
      await fetchCourseRounds();
      if (onScoreLogged) {
        onScoreLogged();
      }
    } catch (error) {
      alert("Error logging round: " + error.message);
      console.error(error);
    } finally {
      if (isMounted.current) setFormLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getScoreColor = (score) => {
    if (rounds.length === 0) return "default";
    const scores = rounds.map((r) => r.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);

    if (score === minScore && score !== maxScore) return "primary";
    if (score === maxScore) return "secondary";
    return "default";
  };

  return (
    <>
      <Typography variant="h6" style={{ paddingBottom: '16px', marginTop: 64 }}>
        Rounds ({rounds.length})
      </Typography>
      <Box className={classes.roundsContainer}>
        {rounds.length > 0 ? (
        <TableContainer component={Paper} className={classes.roundsTable} style={{ maxHeight: rounds.length >= 5 ? "400px" : "auto", overflowY: rounds.length >= 5 ? "auto" : "visible" }}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell className={classes.tableHeader}>Player</TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Score
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Holes
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Date
                </TableCell>
                <TableCell className={classes.tableHeader}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rounds.map((round, index) => (
                <TableRow
                  key={index}
                  className={
                    getScoreColor(round.score) === "primary"
                      ? classes.lowScoreRow
                      : ""
                  }
                >
                  <TableCell>
                    <Box className={classes.userCell}>
                      <Avatar style={{ width: 32, height: 32 }}>
                        {round.userName
                          ? round.userName.charAt(0).toUpperCase()
                          : "?"}
                      </Avatar>
                      <span>
                        {round.userName || "Anonymous Player"}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={round.score}
                      color={
                        getScoreColor(round.score) === "primary"
                          ? "primary"
                          : "default"
                      }
                      variant="outlined"
                      className={classes.scoreCell}
                    />
                  </TableCell>
                  <TableCell align="center">{round.holes}</TableCell>
                  <TableCell align="center">{formatDate(round.date)}</TableCell>
                  <TableCell>
                    {round.notes ? (
                      <span title={round.notes}>
                        {round.notes.substring(0, 30)}
                        {round.notes.length > 30 ? "..." : ""}
                      </span>
                    ) : (
                      <span style={{ color: "#999" }}>—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box className={classes.noRoundsMessage}>
          <Typography variant="body2" color="textSecondary">
            {loading ? "Loading rounds..." : "No rounds logged yet. Be the first!"}
          </Typography>
        </Box>
      )}

      {user?.result?.name ? (
        <Box style={{ marginTop: "16px" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Log a Round
          </Button>
        </Box>
      ) : (
        <Box className={classes.loginPrompt} style={{ marginTop: "16px" }}>
          <Typography variant="body2" color="textSecondary">
            <Typography
              component={Link}
              to="/auth"
              className={globalClasses.logout}
              variant="inherit"
              color="primary"
            >
              Sign in
            </Typography>
            {" "}to log your scores for this course
          </Typography>
        </Box>
      )}
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth disableEnforceFocus>
        <DialogTitle>Log Your Round at {course.name}</DialogTitle>
        <DialogContent>
          <Box style={{ marginTop: 16 }}>
            <TextField
              label="Score"
              type="number"
              name="score"
              value={formData.score}
              onChange={handleInputChange}
              fullWidth
              className={classes.formField}
              variant="outlined"
              inputProps={{ min: "0" }}
              helperText="Enter your total score for the round"
            />

            <FormControl fullWidth className={classes.formField} variant="outlined">
              <InputLabel>Holes</InputLabel>
              <Select
                name="holes"
                value={formData.holes}
                onChange={handleInputChange}
                label="Holes"
                disabled={(course?.holes === "9" || course?.holes === 9)}
              >
                <MenuItem value="9">9 Holes</MenuItem>
                {(course?.holes === "18" || course?.holes === 18 || !course?.holes) && (
                  <MenuItem value="18">18 Holes</MenuItem>
                )}
              </Select>
            </FormControl>

            {course.teeBoxes && course.teeBoxes.length > 0 && (
              <FormControl fullWidth className={classes.formField} variant="outlined">
                <InputLabel>Tee Box</InputLabel>
                <Select
                  value={useCustomRatings ? "other" : selectedTeeIndex}
                  onChange={(e) => {
                    if (e.target.value === "other") {
                      setUseCustomRatings(true);
                    } else {
                      setUseCustomRatings(false);
                      setSelectedTeeIndex(e.target.value);
                    }
                  }}
                  label="Tee Box"
                >
                  {course.teeBoxes.map((tee, index) => (
                    <MenuItem key={index} value={index}>
                      {tee.tee || `Tee ${index + 1}`} (Slope: {tee.slope}, Handicap: {tee.handicap})
                    </MenuItem>
                  ))}
                  <MenuItem value="other">Other (Enter custom ratings)</MenuItem>
                </Select>
              </FormControl>
            )}

            {useCustomRatings && (
              <>
                <TextField
                  label="Slope Rating"
                  type="number"
                  value={customSlope}
                  onChange={(e) => setCustomSlope(e.target.value)}
                  fullWidth
                  className={classes.formField}
                  variant="outlined"
                  inputProps={{ step: "0.1", min: "0" }}
                  helperText="Enter the slope rating for this tee"
                />
                <TextField
                  label="Course Handicap (Rating)"
                  type="number"
                  value={customHandicap}
                  onChange={(e) => setCustomHandicap(e.target.value)}
                  fullWidth
                  className={classes.formField}
                  variant="outlined"
                  inputProps={{ step: "0.1", min: "0" }}
                  helperText="Enter the course handicap/rating for this tee"
                />
              </>
            )}

            <TextField
              label="Date"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              fullWidth
              className={classes.formField}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Notes (Optional)"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              fullWidth
              className={classes.formField}
              variant="outlined"
              multiline
              minRows={3}
              placeholder="Weather, playing conditions, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={formLoading}
          >
            {formLoading ? "Logging..." : "Log Round"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CourseRoundsSection;
